<script lang="ts" setup>
import { watch, onUnmounted } from 'vue'

import IconCloseGray from '@/assets/images/icon/close-gray.svg'
import ABaseIcon from '@/components/atoms/ABaseIcon/index.vue'

/**
 * Types
 */
type Props = {
  isOpened: boolean
}
const props = withDefaults(defineProps<Props>(), {
  isOpened: false,
})

/**
 * Emits
 */
type Emits = {
  close: []
}

const emit = defineEmits<Emits>()

/**
 * Watchers
 */
watch(
  () => props.isOpened,
  (newVal) => {
    if (newVal) {
      // props.isOpened が true なら背景を固定
      document.documentElement.style.overflow = 'hidden'
    }
    else {
      // props.isOpened が false なら背景を解除
      document.documentElement.style.overflow = 'auto'
    }
  },
)

/**
 * Lifecycle
 */
onUnmounted(() => {
  // コンポーネントがアンマウントされたら背景を解除
  if (document.documentElement.style.overflow === 'hidden') {
    document.documentElement.style.overflow = 'auto'
  }
})
</script>

<template>
  <div
    class="modal"
    :class="{ visible: props.isOpened === true }"
  >
    <div
      class="overlay-scrim"
      @click="() => emit('close')"
    />

    <div class="overlay-container">
      <div class="title">
        <slot name="title" />

        <button
          class="button-close"
          @click="() => emit('close')"
        >
          <ABaseIcon :src="IconCloseGray" />
        </button>
      </div>

      <div class="content">
        <slot name="content" />
      </div>

      <div
        v-if="$slots.button"
        class="button"
      >
        <slot name="button" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal {
  position: relative;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline-end: 0;
  overflow: auto;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.5s, visibility 0.5s;

  .overlay {
    &-scrim {
      position: fixed;
      inset: 0;
      overflow: hidden;
      pointer-events: auto;
      background: rgb(var(--v-theme-ui-black));
      border-radius: inherit;
      opacity: 0.32;
    }

    &-container {
      position: absolute;
      left: 50%;
      width: 480px;
      color: rgb(var(--v-theme-ui-text-black));
      pointer-events: auto;
      background: rgb(var(--v-theme-ui-white));
      border: 1px solid rgb(var(--v-theme-ui-gray1));
      border-radius: 8px;
      box-shadow: 0 0 8px rgba(var(--v-theme-ui-black), 16%);
      transform: translateX(-50%);

      .title {
        position: relative;
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
        min-height: 36px;
        padding: 8px 16px;
        font-size: 12px;
        border-bottom: 1px solid rgb(var(--v-theme-secondary));
      }

      :deep(.card-title) {
        font-size: 12px;
        font-weight: normal;
      }

      .button-close {
        width: 20px;
        height: 20px;
      }

      .content {
        min-height: 120px;
        padding: 16px 24px;
      }

      .button {
        display: flex;
        align-items: flex-end;
        align-items: center;
        justify-content: center;
        min-height: 52px;
        padding: 0.5rem;
      }
    }
  }

  &.visible {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.5s, visibility 0.5s;
  }
}

html .modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

html :not(.modal.visible) {
  overflow: hidden;
}
</style>
